import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './CryptoPayment.css';

const COINS_PER_USD = 120;

const CRYPTO_COINS = [
  { id: 'btc', name: 'Bitcoin', symbol: 'BTC', icon: '₿', color: '#f7931a' },
  { id: 'eth', name: 'Ethereum', symbol: 'ETH', icon: 'Ξ', color: '#627eea' },
  { id: 'usdt', name: 'Tether', symbol: 'USDT', icon: '₮', color: '#26a17b' },
  { id: 'ltc', name: 'Litecoin', symbol: 'LTC', icon: 'Ł', color: '#bfbbbb' },
];

const CryptoPayment = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // --- Commented out: package-related state (to be restored later) ---
  // const [products, setProducts] = useState([]);
  // const [selectedProduct, setSelectedProduct] = useState(null);
  // const [paymentMode, setPaymentMode] = useState('pack');
  // const [loading, setLoading] = useState(true);

  const [customAmount, setCustomAmount] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('btc');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth');
      return;
    }

    // --- Commented out: product fetching (to be restored later) ---
    // const fetchProducts = async () => {
    //   try {
    //     const response = await api.get('/v1/products', true);
    //     if (response?.data) {
    //       const productList = Array.isArray(response.data) ? response.data : response.data.products || [];
    //       setProducts(productList);
    //       if (productList.length > 0) {
    //         setSelectedProduct(productList[0]);
    //       }
    //     }
    //   } catch (err) {
    //     console.error('Failed to fetch products:', err);
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    // fetchProducts();
  }, []);

  const handlePurchase = async () => {
    if (processing) return;

    const amount = parseFloat(customAmount);
    if (!amount || amount < 1) {
      setError('Please enter a valid amount (minimum $1)');
      return;
    }

    // --- Commented out: pack mode validation (to be restored later) ---
    // if (paymentMode === 'pack' && !selectedProduct) {
    //   setError('Please select a package');
    //   return;
    // }

    setProcessing(true);
    setError('');

    try {
      const coins = Math.floor(parseFloat(customAmount) * COINS_PER_USD);
      const body = {
        coins,
        amount: customAmount,
        crypto: selectedCrypto,
        platform: 'WEB',
      };

      // --- Commented out: pack mode body (to be restored later) ---
      // const body =
      //   paymentMode === 'pack'
      //     ? {
      //         productId: selectedProduct.serviceProductId,
      //         crypto: selectedCrypto,
      //         platform: 'WEB',
      //       }
      //     : {
      //         amount: parseInt(customAmount),
      //         crypto: selectedCrypto,
      //         platform: 'WEB',
      //       };

      const response = await api.post('/v1/payments/cryptomus', body, true);

      if (response?.data?.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      } else {
        setError('Failed to create payment. Please try again.');
      }
    } catch (err) {
      setError(err.data?.message || err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // --- Commented out: loading state for product fetch (to be restored later) ---
  // if (loading) {
  //   return (
  //     <div className="crypto-page">
  //       <div className="crypto-container">
  //         <div className="crypto-loading">
  //           <div className="crypto-spinner"></div>
  //           <p>Loading payment options...</p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="crypto-page">
      <div className="crypto-container">
        {/* Header */}
        <div className="crypto-header">
          <div className="crypto-header-text">
            <h1 className="crypto-title">Pay with Cryptocurrency</h1>
            <p className="crypto-subtitle">
              Add funds to your account using your preferred cryptocurrency
            </p>
          </div>
          <div className="crypto-secure-badge">
            <span className="material-symbols-outlined">verified_user</span>
            <span>Secure Payment</span>
          </div>
        </div>

        <div className="crypto-grid">
          {/* Left Column - Payment Options */}
          <div className="crypto-main">
            {/* Step 1: Select Crypto */}
            <div className="crypto-card">
              <div className="crypto-card-header">
                <span className="step-number">1</span>
                <h2 className="crypto-card-title">Select Cryptocurrency</h2>
              </div>
              <div className="crypto-coins-grid">
                {CRYPTO_COINS.map((coin) => (
                  <button
                    key={coin.id}
                    className={`crypto-coin-btn ${selectedCrypto === coin.id ? 'active' : ''}`}
                    onClick={() => setSelectedCrypto(coin.id)}
                  >
                    <span
                      className="crypto-coin-icon"
                      style={{
                        backgroundColor: `${coin.color}15`,
                        color: coin.color,
                      }}
                    >
                      {coin.icon}
                    </span>
                    <span className="crypto-coin-name">{coin.name}</span>
                    <span className="crypto-coin-symbol">{coin.symbol}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Enter Amount (custom only) */}
            <div className="crypto-card">
              <div className="crypto-card-header">
                <span className="step-number">2</span>
                <h2 className="crypto-card-title">Enter Amount</h2>
              </div>

              {/* --- Commented out: mode toggle (to be restored later) ---
              <div className="amount-mode-toggle">
                <button
                  className={`mode-btn ${paymentMode === 'pack' ? 'active' : ''}`}
                  onClick={() => setPaymentMode('pack')}
                >
                  <span className="material-symbols-outlined">inventory_2</span>
                  Coin Packages
                </button>
                <button
                  className={`mode-btn ${paymentMode === 'custom' ? 'active' : ''}`}
                  onClick={() => setPaymentMode('custom')}
                >
                  <span className="material-symbols-outlined">tune</span>
                  Custom Amount
                </button>
              </div>
              */}

              {/* --- Commented out: packages grid (to be restored later) ---
              {paymentMode === 'pack' ? (
                <div className="packages-grid">
                  {products.length === 0 ? (
                    <div className="no-packages">
                      <span className="material-symbols-outlined">inventory_2</span>
                      <p>No packages available at the moment</p>
                    </div>
                  ) : (
                    products.map((product) => (
                      <button
                        key={product.serviceProductId}
                        className={`package-card ${selectedProduct?.serviceProductId === product.serviceProductId ? 'active' : ''}`}
                        onClick={() => setSelectedProduct(product)}
                      >
                        {product.discount > 1 && (
                          <span className="package-badge">
                            Save {Math.round((1 - 1 / product.discount) * 100)}%
                          </span>
                        )}
                        <div className="package-coins">
                          <span className="material-symbols-outlined">toll</span>
                          <span className="package-coins-amount">
                            {product.coins.toLocaleString()}
                          </span>
                        </div>
                        <p className="package-coins-label">coins</p>
                        <div className="package-price">
                          ${product.price.toFixed(2)}
                        </div>
                        <p className="package-rate">
                          ${(product.price / product.coins).toFixed(4)} / coin
                        </p>
                      </button>
                    ))
                  )}
                </div>
              ) : ( */}
              <div className="custom-amount-section">
                <label className="custom-amount-label">Enter amount in USD (minimum $1)</label>
                <div className="custom-amount-input-wrapper">
                  <span className="input-prefix-icon">$</span>
                  <input
                    type="number"
                    className="custom-amount-input"
                    placeholder="e.g. 5"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setError('');
                    }}
                    min="1"
                    step="1"
                  />
                  <span className="input-suffix">USD</span>
                </div>
                {customAmount && parseFloat(customAmount) >= 1 && (
                  <p className="coin-conversion">
                    You will receive <strong>{Math.floor(parseFloat(customAmount) * COINS_PER_USD).toLocaleString()}</strong> coins
                  </p>
                )}
                <div className="quick-amounts">
                  {[1, 5, 10, 25, 50].map((amount) => (
                    <button
                      key={amount}
                      className={`quick-amount-btn ${customAmount === String(amount) ? 'active' : ''}`}
                      onClick={() => setCustomAmount(String(amount))}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <p className="exchange-rate-note">1 USD = {COINS_PER_USD} coins</p>
              </div>
              {/* )} */}
            </div>
          </div>

          {/* Right Column - Summary */}
          <aside className="crypto-sidebar">
            <div className="crypto-card summary-card">
              <h3 className="summary-title">Order Summary</h3>

              <div className="summary-crypto-row">
                <span
                  className="summary-crypto-icon"
                  style={{
                    backgroundColor: `${CRYPTO_COINS.find((c) => c.id === selectedCrypto)?.color}15`,
                    color: CRYPTO_COINS.find((c) => c.id === selectedCrypto)?.color,
                  }}
                >
                  {CRYPTO_COINS.find((c) => c.id === selectedCrypto)?.icon}
                </span>
                <span className="summary-crypto-name">
                  Paying with {CRYPTO_COINS.find((c) => c.id === selectedCrypto)?.name}
                </span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-details">
                <div className="summary-row">
                  <span className="summary-label">Amount</span>
                  <span className="summary-value">
                    {customAmount ? `$${parseFloat(customAmount).toFixed(2)}` : '$0.00'}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Coins</span>
                  <span className="summary-value">
                    {customAmount && parseFloat(customAmount) >= 1
                      ? Math.floor(parseFloat(customAmount) * COINS_PER_USD).toLocaleString()
                      : '0'}
                  </span>
                </div>
                {/* --- Commented out: pack summary (to be restored later) ---
                {paymentMode === 'pack' && selectedProduct && (
                  <div className="summary-row">
                    <span className="summary-label">Package</span>
                    <span className="summary-value">{selectedProduct.name}</span>
                  </div>
                )}
                */}
              </div>

              <div className="summary-divider"></div>

              <div className="summary-total-row">
                <span className="summary-total-label">Total</span>
                <span className="summary-total-value">
                  {customAmount && parseFloat(customAmount) >= 1
                    ? `$${parseFloat(customAmount).toFixed(2)}`
                    : '$0.00'}
                </span>
              </div>

              {error && (
                <div className="summary-error">
                  <span className="material-symbols-outlined">error</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                className="pay-btn"
                onClick={handlePurchase}
                disabled={processing || !customAmount}
              >
                {processing ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">lock</span>
                    <span>Pay with Crypto</span>
                  </>
                )}
              </button>

              <p className="summary-note">
                You will be redirected to a secure payment gateway to complete your transaction.
              </p>

              <div className="summary-trust">
                <div className="trust-item">
                  <span className="material-symbols-outlined">shield</span>
                  <span>Encrypted</span>
                </div>
                <div className="trust-item">
                  <span className="material-symbols-outlined">speed</span>
                  <span>Instant Credit</span>
                </div>
                <div className="trust-item">
                  <span className="material-symbols-outlined">support_agent</span>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CryptoPayment;
